import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import contractService from "../../services/contractService";
import LoadingSpinner from "../common/LoadingSpinner";
import { formatAmount, formatDate } from "../../utils/formatters";

// 🆕 NOUVEAU: Composant Modal pour les détails du contrat
const ContractDetailsModal = ({ isOpen, onClose, contractNumber }) => {
  const [loading, setLoading] = useState(false);
  const [contractDetails, setContractDetails] = useState(null);
  const [error, setError] = useState(null);

  // Charger les détails du contrat
  useEffect(() => {
    if (isOpen && contractNumber) {
      setLoading(true);
      setError(null);
      
      const loadContractDetails = async () => {
        try {
          // Rechercher le contrat par son numéro
          const result = await contractService.getContracts({
            search: contractNumber,
            limit: 1
          });
          
          if (result.success && result.data && result.data.length > 0) {
            setContractDetails(result.data[0]);
          } else {
            setError("Contrat non trouvé");
          }
        } catch (error) {
          console.error("Erreur lors du chargement du contrat:", error);
          setError("Erreur lors du chargement du contrat");
        } finally {
          setLoading(false);
        }
      };

      loadContractDetails();
    }
  }, [isOpen, contractNumber]);

  // Fermer avec Échap
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* En-tête */}
          <div className="bg-blue-50 px-6 py-4 border-b border-blue-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-blue-900">
                💰 Détails des versements - {contractNumber}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Contenu */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <LoadingSpinner message="Chargement des détails..." />
              </div>
            ) : error ? (
              <div className="text-center text-red-600 py-8">
                <div className="text-4xl mb-2">😟</div>
                <p>{error}</p>
              </div>
            ) : contractDetails ? (
              <div className="space-y-6">
                {/* Résumé financier */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-blue-600">
                      {formatAmount(contractDetails.expectedAmount || 0)}
                    </div>
                    <div className="text-sm text-blue-600">Montant Prévu</div>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-green-600">
                      {formatAmount(contractDetails.receivedAmount || 0)}
                    </div>
                    <div className="text-sm text-green-600">Total Perçu</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-orange-600">
                      {formatAmount(contractDetails.remainingAmount || 0)}
                    </div>
                    <div className="text-sm text-orange-600">Reste à Percevoir</div>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <div className="text-lg font-bold text-purple-600">
                      {contractDetails.versements ? contractDetails.versements.length : 0}
                    </div>
                    <div className="text-sm text-purple-600">Nb Versements</div>
                  </div>
                </div>

                {/* Informations générales */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold mb-3">Informations générales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium">Agence:</span> {contractDetails.agency}</div>
                    <div><span className="font-medium">Client:</span> {contractDetails.client || 'N/A'}</div>
                    <div><span className="font-medium">Date création:</span> {formatDate(contractDetails.creationDate)}</div>
                    <div><span className="font-medium">Date limite:</span> {formatDate(contractDetails.limitDate)}</div>
                    <div><span className="font-medium">Poids bijou:</span> {contractDetails.jewelWeight || 0} g</div>
                    <div><span className="font-medium">Montant décaissé:</span> {formatAmount(contractDetails.disbursementAmount || 0)}</div>
                  </div>
                </div>

                {/* Liste des versements */}
                <div className="bg-white border border-gray-200 rounded-lg">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-lg font-semibold">
                      📋 Détail des versements
                      {contractDetails.versements && contractDetails.versements.length > 0 && (
                        <span className="text-sm font-normal text-gray-600 ml-2">
                          ({contractDetails.versements.length} versement{contractDetails.versements.length > 1 ? 's' : ''})
                        </span>
                      )}
                    </h3>
                  </div>
                  
                  {contractDetails.versements && contractDetails.versements.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full table-auto">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">N°</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Date</th>
                            <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Montant</th>
                            <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">Type VP</th>
                            <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Dégagement</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">N° Reçu</th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Remarque</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contractDetails.versements
                            .sort((a, b) => (a.ordreVers || 0) - (b.ordreVers || 0))
                            .map((versement, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}>
                              <td className="px-4 py-2 text-sm font-medium">{versement.ordreVers || '-'}</td>
                              <td className="px-4 py-2 text-sm text-center">
                                {versement.dateVerse ? formatDate(versement.dateVerse) : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm text-right font-medium text-green-600">
                                {formatAmount(versement.montantPercu || 0)}
                              </td>
                              <td className="px-4 py-2 text-sm text-center">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {versement.typeVP || '-'}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-sm text-right">
                                {versement.degagement ? formatAmount(versement.degagement) : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm">{versement.numeroRecu || '-'}</td>
                              <td className="px-4 py-2 text-sm">{versement.remarqueVersement || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Total des versements */}
                      <div className="border-t border-gray-200 bg-blue-50 px-4 py-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-blue-800">Total des versements:</span>
                          <span className="text-lg font-bold text-blue-600">
                            {formatAmount(
                              contractDetails.versements.reduce((sum, v) => sum + (parseFloat(v.montantPercu) || 0), 0)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <div className="text-4xl mb-2">📭</div>
                      <p>Aucun versement enregistré pour ce contrat</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <div className="text-4xl mb-2">📄</div>
                <p>Aucune donnée disponible</p>
              </div>
            )}
          </div>

          {/* Pied de page */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DailyStatesTab = ({ initialFilters = {} }) => {
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // 🆕 NOUVEAU: État pour la modal des détails
  const [modalState, setModalState] = useState({
    isOpen: false,
    contractNumber: null
  });

  // États séparés pour les différents niveaux de chargement
  const [loadingStates, setLoadingStates] = useState({
    base: false, // Agences + années
    months: false, // Mois pour année
    days: false, // Jours pour mois
    data: false, // Données principales
  });

  // Options de filtres avec structure optimisée
  const [filterOptions, setFilterOptions] = useState({
    agencies: [],
    years: [],
    months: [],
    days: [],
  });

  // États des filtres
  const [filters, setFilters] = useState({
    agency: initialFilters.agency || "",
    year: initialFilters.year || new Date().getFullYear().toString(),
    month: initialFilters.month || (new Date().getMonth() + 1).toString(),
    day: new Date().getDate().toString(),
  });

  // Ref pour gérer le montage et les timeouts
  const componentRef = useRef({
    isMounted: true,
    requestQueue: new Map(),
    timeouts: new Map(),
  });

  // 🆕 NOUVEAU: Gestionnaires pour la modal
  const handleContractDoubleClick = useCallback((contractNumber) => {
    console.log("🖱️ Double-clic sur contrat:", contractNumber);
    setModalState({
      isOpen: true,
      contractNumber: contractNumber
    });
  }, []);

  const handleModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      contractNumber: null
    });
  }, []);

  // Nettoyage complet lors du démontage - CORRIGÉ pour ESLint
  useEffect(() => {
    const current = componentRef.current;
    current.isMounted = true;

    return () => {
      current.isMounted = false;

      // Nettoyer tous les timeouts
      current.timeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      current.timeouts.clear();

      // Nettoyer la queue de requêtes
      current.requestQueue.clear();
    };
  }, []);

  // Fonction helper sécurisée pour les mises à jour d'état
  const safeSetState = useCallback((updater, key = "default") => {
    const current = componentRef.current;
    if (!current.isMounted) return;

    // Utiliser setTimeout pour différer les mises à jour et éviter les conflits
    const timeoutId = setTimeout(() => {
      if (current.isMounted) {
        updater();
      }
      current.timeouts.delete(key);
    }, 0);

    // Nettoyer le timeout précédent s'il existe
    const previousTimeout = current.timeouts.get(key);
    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    current.timeouts.set(key, timeoutId);
  }, []);

  // Fonction utilitaire pour mettre à jour les états de chargement de manière sécurisée
  const updateLoadingState = useCallback(
    (key, value) => {
      safeSetState(() => {
        setLoadingStates((prev) => ({ ...prev, [key]: value }));
      }, `loading-${key}`);
    },
    [safeSetState]
  );

  // Système de requêtes simplifié et sécurisé
  const makeRequest = useCallback(
    async (requestKey, requestFn, loadingKey, params = {}) => {
      const current = componentRef.current;
      if (!current.isMounted) return null;

      // Annuler la requête précédente du même type
      const previousRequest = current.requestQueue.get(requestKey);
      if (previousRequest) {
        previousRequest.cancelled = true;
      }

      // Créer une nouvelle requête
      const currentRequest = { cancelled: false };
      current.requestQueue.set(requestKey, currentRequest);

      updateLoadingState(loadingKey, true);

      try {
        const result = await requestFn(params);

        // Vérifier si la requête n'a pas été annulée et le composant est monté
        if (current.isMounted && !currentRequest.cancelled) {
          return result;
        }
        return null;
      } catch (error) {
        console.error(`Erreur requête ${requestKey}:`, error);
        throw error;
      } finally {
        if (current.isMounted && !currentRequest.cancelled) {
          updateLoadingState(loadingKey, false);
        }
        current.requestQueue.delete(requestKey);
      }
    },
    [updateLoadingState]
  );

  // Fonction de tri par agence puis par dossier
  const sortByAgencyAndDossier = useCallback((contracts) => {
    if (!contracts || !Array.isArray(contracts)) return [];

    return [...contracts].sort((a, b) => {
      // Premier tri : par agence (ordre alphabétique)
      const agencyComparison = (a.agency || "").localeCompare(
        b.agency || "",
        "fr",
        {
          numeric: true,
          caseFirst: "upper",
        }
      );

      if (agencyComparison !== 0) {
        return agencyComparison;
      }

      // Deuxième tri : par numéro de dossier (ordre numérique croissant)
      const dossierA = parseInt(a.dossier) || 0;
      const dossierB = parseInt(b.dossier) || 0;

      return dossierA - dossierB;
    });
  }, []);

  // Tri pour les états de versement (utilise contractNumber si pas de dossier)
  const sortPaymentStatesByAgencyAndContract = useCallback((paymentStates) => {
    if (!paymentStates || !Array.isArray(paymentStates)) return [];

    return [...paymentStates].sort((a, b) => {
      // Premier tri : par agence (ordre alphabétique)
      const agencyComparison = (a.agency || "").localeCompare(
        b.agency || "",
        "fr",
        {
          numeric: true,
          caseFirst: "upper",
        }
      );

      if (agencyComparison !== 0) {
        return agencyComparison;
      }

      // Deuxième tri : par numéro de contrat (ordre alphabétique avec gestion numérique)
      const contractA = a.contractNumber || "";
      const contractB = b.contractNumber || "";

      return contractA.localeCompare(contractB, "fr", {
        numeric: true,
        caseFirst: "upper",
      });
    });
  }, []);

  // Données triées mémorisées
  const sortedData = useMemo(() => {
    if (!data) return null;

    return {
      ...data,
      contractsCreated: sortByAgencyAndDossier(data.contractsCreated),
      paymentStates: sortPaymentStatesByAgencyAndContract(data.paymentStates),
    };
  }, [data, sortByAgencyAndDossier, sortPaymentStatesByAgencyAndContract]);

  // 1. CHARGEMENT INITIAL - Version corrigée pour ESLint
  useEffect(() => {
    const loadBaseOptions = async () => {
      try {
        console.log("🚀 Chargement options de base");

        const result = await makeRequest(
          "base-options",
          (params) => contractService.getDailyStatesFilterOptions(params),
          "base",
          {}
        );

        if (result && result.success) {
          safeSetState(() => {
            setFilterOptions((prev) => ({
              ...prev,
              agencies: result.data.agencies || [],
              years: result.data.years || [],
            }));
          }, "base-options-update");

          console.log("✅ Options de base chargées:", {
            agencies: result.data.agencies?.length,
            years: result.data.years?.length,
          });
        } else {
          console.error("❌ Réponse non réussie:", result);
          safeSetState(
            () => setError("Aucune donnée reçue du serveur"),
            "base-error"
          );
        }
      } catch (error) {
        console.error("❌ Erreur options de base:", error);
        safeSetState(
          () =>
            setError(
              "Erreur lors du chargement des options de base: " + error.message
            ),
          "base-error-2"
        );
      }
    };

    loadBaseOptions();
  }, [makeRequest, safeSetState]);

  // 2. CHARGEMENT DES MOIS - Version corrigée pour ESLint
  useEffect(() => {
    const currentRef = componentRef.current;

    if (!filters.year) {
      safeSetState(() => {
        setFilterOptions((prev) => ({ ...prev, months: [], days: [] }));
      }, "clear-months");
      return;
    }

    const loadMonths = async () => {
      try {
        console.log(`🚀 Chargement mois pour ${filters.year}`);

        const result = await makeRequest(
          "months-options",
          (params) => contractService.getDailyStatesFilterOptions(params),
          "months",
          { year: filters.year }
        );

        if (result && result.success) {
          safeSetState(() => {
            setFilterOptions((prev) => ({
              ...prev,
              months: result.data.months || [],
              days: [],
            }));
          }, "months-update");

          console.log(
            `✅ Mois chargés pour ${filters.year}:`,
            result.data.months?.length
          );
        }
      } catch (error) {
        console.error("❌ Erreur chargement mois:", error);
      }
    };

    const timeoutKey = "load-months";
    const previousTimeout = currentRef.timeouts.get(timeoutKey);
    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    const timeoutId = setTimeout(loadMonths, 300);
    currentRef.timeouts.set(timeoutKey, timeoutId);

    return () => {
      const timeout = currentRef.timeouts.get(timeoutKey);
      if (timeout) {
        clearTimeout(timeout);
        currentRef.timeouts.delete(timeoutKey);
      }
    };
  }, [filters.year, makeRequest, safeSetState]);

  // 3. CHARGEMENT DES JOURS - Version corrigée pour ESLint
  useEffect(() => {
    const currentRef = componentRef.current;

    if (!filters.year || !filters.month) {
      safeSetState(() => {
        setFilterOptions((prev) => ({ ...prev, days: [] }));
      }, "clear-days");
      return;
    }

    const loadDays = async () => {
      try {
        console.log(
          `🚀 Chargement jours pour ${filters.year}/${filters.month}`
        );

        const result = await makeRequest(
          "days-options",
          (params) => contractService.getDailyStatesFilterOptions(params),
          "days",
          { year: filters.year, month: filters.month }
        );

        if (result && result.success) {
          safeSetState(() => {
            setFilterOptions((prev) => ({
              ...prev,
              days: result.data.days || [],
            }));
          }, "days-update");

          console.log(
            `✅ Jours chargés pour ${filters.year}/${filters.month}:`,
            result.data.days?.length
          );
        }
      } catch (error) {
        console.error("❌ Erreur chargement jours:", error);
      }
    };

    const timeoutKey = "load-days";
    const previousTimeout = currentRef.timeouts.get(timeoutKey);
    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    const timeoutId = setTimeout(loadDays, 300);
    currentRef.timeouts.set(timeoutKey, timeoutId);

    return () => {
      const timeout = currentRef.timeouts.get(timeoutKey);
      if (timeout) {
        clearTimeout(timeout);
        currentRef.timeouts.delete(timeoutKey);
      }
    };
  }, [filters.year, filters.month, makeRequest, safeSetState]);

  // 4. CHARGEMENT DES DONNÉES PRINCIPALES - Version corrigée pour ESLint
  useEffect(() => {
    const currentRef = componentRef.current;

    if (!filters.year || !filters.month || !filters.day) {
      safeSetState(() => setData(null), "clear-data");
      return;
    }

    const loadData = async () => {
      try {
        console.log(
          `🚀 Chargement données pour ${filters.day}/${filters.month}/${filters.year}`
        );

        safeSetState(() => setError(null), "clear-error");

        const result = await makeRequest(
          "main-data",
          (params) => contractService.getDailyStates(params),
          "data",
          filters
        );

        if (result && result.success) {
          safeSetState(() => setData(result.data), "data-update");
          console.log("✅ Données chargées:", result.data);
        } else if (result) {
          safeSetState(
            () =>
              setError(
                result.message || "Erreur lors du chargement des données"
              ),
            "data-error"
          );
        }
      } catch (error) {
        console.error("❌ Erreur chargement données:", error);
        safeSetState(
          () =>
            setError("Erreur lors du chargement des données: " + error.message),
          "data-error-2"
        );
      }
    };

    const timeoutKey = "load-data";
    const previousTimeout = currentRef.timeouts.get(timeoutKey);
    if (previousTimeout) {
      clearTimeout(previousTimeout);
    }

    const timeoutId = setTimeout(loadData, 500);
    currentRef.timeouts.set(timeoutKey, timeoutId);

    return () => {
      const timeout = currentRef.timeouts.get(timeoutKey);
      if (timeout) {
        clearTimeout(timeout);
        currentRef.timeouts.delete(timeoutKey);
      }
    };
  }, [filters, makeRequest, safeSetState]);

  // Gestionnaire de changement de filtres - Version simplifiée
  const handleFilterChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFilters((prev) => {
        const newFilters = { ...prev, [name]: value };

        if (name === "year") {
          newFilters.month = "";
          newFilters.day = "";
        } else if (name === "month") {
          newFilters.day = "";
        }

        return newFilters;
      });

      if (name === "year") {
        safeSetState(() => {
          setFilterOptions((prev) => ({
            ...prev,
            months: [],
            days: [],
          }));
        }, "reset-year-options");
      } else if (name === "month") {
        safeSetState(() => {
          setFilterOptions((prev) => ({
            ...prev,
            days: [],
          }));
        }, "reset-month-options");
      }
    },
    [safeSetState]
  );

  // Fonction pour forcer le rechargement
  const forceReload = useCallback(() => {
    const current = componentRef.current;

    current.timeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    current.timeouts.clear();

    safeSetState(() => {
      setError(null);
      setData(null);
      setLoadingStates((prev) => ({ ...prev, data: false }));
    }, "force-reload");

    setFilters((currentFilters) => ({
      ...currentFilters,
      _forceReload: Date.now(),
    }));
  }, [safeSetState]);

  // Formatters memoizés
  const formatDecimal = useMemo(
    () => (value) => {
      if (value === null || value === undefined) return "0,00";
      return Number(parseFloat(value).toFixed(2)).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    []
  );

  const getSafeValue = useMemo(
    () =>
      (obj, path, defaultValue = 0) => {
        try {
          return (
            path.split(".").reduce((acc, key) => acc?.[key], obj) ??
            defaultValue
          );
        } catch (error) {
          return defaultValue;
        }
      },
    []
  );

  // Indicateurs de chargement
  const isLoading = loadingStates.base || loadingStates.data;
  const isFiltersLoading = loadingStates.months || loadingStates.days;

  // Rendu conditionnel pour les erreurs
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">😟</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Erreur de chargement
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={forceReload}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h3 className="text-xl font-semibold mb-4">États journaliers</h3>

     {/* 🆕 NOUVEAU: Modal des détails du contrat */}
     <ContractDetailsModal
       isOpen={modalState.isOpen}
       onClose={handleModalClose}
       contractNumber={modalState.contractNumber}
     />

     {/* Filtres optimisés */}
     <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
       <h4 className="text-lg font-semibold mb-3">
         Filtres
         {isFiltersLoading && (
           <span className="ml-2 text-sm text-blue-600 animate-pulse">
             🔄 Chargement...
           </span>
         )}
       </h4>

       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
         {/* Filtre Agence */}
         <div>
           <label
             htmlFor="agency"
             className="block text-sm font-medium text-gray-700 mb-1"
           >
             Agence
           </label>
           <select
             id="agency"
             name="agency"
             value={filters.agency}
             onChange={handleFilterChange}
             disabled={loadingStates.base}
             className="block w-full px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md disabled:bg-gray-100 disabled:cursor-wait"
           >
             <option value="">Toutes les agences</option>
             {filterOptions.agencies.map((agency, index) => (
               <option key={`agency-${agency}-${index}`} value={agency}>
                 {agency}
               </option>
             ))}
           </select>
           {loadingStates.base && (
             <div className="text-xs text-gray-500 mt-1">
               Chargement des agences...
             </div>
           )}
           {!loadingStates.base && filterOptions.agencies.length === 0 && (
             <div className="text-xs text-red-500 mt-1">
               Aucune agence trouvée
             </div>
           )}
         </div>

         {/* Filtre Année */}
         <div>
           <label
             htmlFor="year"
             className="block text-sm font-medium text-gray-700 mb-1"
           >
             Année
           </label>
           <select
             id="year"
             name="year"
             value={filters.year}
             onChange={handleFilterChange}
             disabled={loadingStates.base}
             className="block w-full px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md disabled:bg-gray-100 disabled:cursor-wait"
           >
             <option value="">Sélectionner une année</option>
             {filterOptions.years.map((year, index) => (
               <option key={`year-${year}-${index}`} value={year}>
                 {year}
               </option>
             ))}
           </select>
           {!loadingStates.base && filterOptions.years.length === 0 && (
             <div className="text-xs text-red-500 mt-1">
               Aucune année trouvée
             </div>
           )}
         </div>

         {/* Filtre Mois */}
         <div>
           <label
             htmlFor="month"
             className="block text-sm font-medium text-gray-700 mb-1"
           >
             Mois
             {loadingStates.months && (
               <span className="ml-1 text-xs text-blue-600">🔄</span>
             )}
           </label>
           <select
             id="month"
             name="month"
             value={filters.month}
             onChange={handleFilterChange}
             disabled={loadingStates.months || !filters.year}
             className="block w-full px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md disabled:bg-gray-100 disabled:cursor-wait"
           >
             <option value="">Sélectionner un mois</option>
             {filterOptions.months.map((month, index) => (
               <option
                 key={`month-${month.value}-${index}`}
                 value={month.value}
               >
                 {month.label}
               </option>
             ))}
           </select>
         </div>

         {/* Filtre Jour */}
         <div>
           <label
             htmlFor="day"
             className="block text-sm font-medium text-gray-700 mb-1"
           >
             Jour
             {loadingStates.days && (
               <span className="ml-1 text-xs text-blue-600">🔄</span>
             )}
           </label>
           <select
             id="day"
             name="day"
             value={filters.day}
             onChange={handleFilterChange}
             disabled={loadingStates.days || !filters.year || !filters.month}
             className="block w-full px-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md disabled:bg-gray-100 disabled:cursor-wait"
           >
             <option value="">Sélectionner un jour</option>
             {filterOptions.days.map((day, index) => (
               <option key={`day-${day.value}-${index}`} value={day.value}>
                 {day.label}
               </option>
             ))}
           </select>
         </div>
       </div>

       {/* Informations sur les filtres */}
       <div className="mt-3 text-sm">
         {!filters.year && (
           <p className="text-blue-600">📅 Sélectionnez d'abord une année</p>
         )}
         {filters.year &&
           !filters.month &&
           filterOptions.months.length > 0 && (
             <p className="text-blue-600">
               📅 Sélectionnez un mois ({filterOptions.months.length}{" "}
               disponibles)
             </p>
           )}
         {filters.year &&
           filters.month &&
           !filters.day &&
           filterOptions.days.length > 0 && (
             <p className="text-blue-600">
               📅 Sélectionnez un jour ({filterOptions.days.length}{" "}
               disponibles)
             </p>
           )}
         {filters.year && filters.month && filters.day && (
           <p className="text-green-600">
             ✅ Données pour le {filters.day}/{filters.month}/{filters.year}
             {loadingStates.data && (
               <span className="ml-2 animate-pulse">🔄 Chargement...</span>
             )}
           </p>
         )}
       </div>
     </div>

     {/* Indicateur de chargement principal */}
     {isLoading && (
       <div className="flex items-center justify-center h-32">
         <LoadingSpinner message="Chargement des données..." />
       </div>
     )}

     {/* Utilisation des données triées */}
     {!isLoading && sortedData && (
       <>
         {/* Section Statistiques */}
         <div className="bg-white border border-gray-200 rounded-lg p-4">
           <h4 className="text-lg font-semibold mb-4">
             📊 Statistiques du jour
           </h4>

           <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
             <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
               <div className="text-2xl font-bold text-blue-600">
                 {getSafeValue(sortedData, "statistics.totalContracts")}
               </div>
               <div className="text-sm text-blue-600">Contrats créés</div>
             </div>
             <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
               <div className="text-2xl font-bold text-green-600">
                 {getSafeValue(sortedData, "statistics.agenciesCount")}
               </div>
               <div className="text-sm text-green-600">Agences</div>
             </div>
             <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
               <div className="text-lg font-bold text-purple-600">
                 {formatDecimal(
                   getSafeValue(
                     sortedData,
                     "statistics.totalWeights.jewelWeight"
                   )
                 )}
               </div>
               <div className="text-sm text-purple-600">Poids bijou</div>
             </div>
             <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
               <div className="text-lg font-bold text-red-600">
                 {formatAmount(
                   getSafeValue(
                     sortedData,
                     "statistics.financialTotals.disbursementAmount"
                   )
                 )}
               </div>
               <div className="text-sm text-red-600">Total Décaissé</div>
             </div>
             <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
               <div className="text-lg font-bold text-yellow-700">
                 {formatAmount(
                   getSafeValue(
                     sortedData,
                     "statistics.financialTotals.expectedAmount"
                   )
                 )}
               </div>
               <div className="text-sm text-yellow-700">Montant Prévu</div>
             </div>
           </div>

           {/* Détails financiers en grille responsive */}
           <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
             <div className="bg-gray-50 rounded-lg p-3">
               <h5 className="font-medium text-gray-700 mb-2">
                 Détail des poids
               </h5>
               <div className="space-y-1 text-sm">
                 <div className="flex justify-between">
                   <span>CDVA:</span>
                   <span className="font-medium">
                     {formatDecimal(
                       getSafeValue(
                         sortedData,
                         "statistics.totalWeights.cdvaWeight"
                       )
                     )}
                   </span>
                 </div>
                 <div className="flex justify-between">
                   <span>CDD:</span>
                   <span className="font-medium">
                     {formatDecimal(
                       getSafeValue(
                         sortedData,
                         "statistics.totalWeights.cddWeight"
                       )
                     )}
                   </span>
                 </div>
               </div>
             </div>

             <div className="bg-gray-50 rounded-lg p-3">
               <h5 className="font-medium text-gray-700 mb-2">
                 Informations financières
               </h5>
               <div className="space-y-1 text-sm">
                 <div className="flex justify-between">
                   <span>Reste à percevoir:</span>
                   <span className="font-medium">
                     {formatAmount(
                       getSafeValue(
                         sortedData,
                         "statistics.financialTotals.remainingAmount"
                       )
                     )}
                   </span>
                 </div>
                 <div className="flex justify-between">
                   <span>Remises accordées:</span>
                   <span className="font-medium">
                     {formatAmount(
                       getSafeValue(
                         sortedData,
                         "statistics.financialTotals.discount"
                       )
                     )}
                   </span>
                 </div>
                 <div className="flex justify-between">
                   <span>Montant moyen:</span>
                   <span className="font-medium">
                     {formatAmount(
                       getSafeValue(
                         sortedData,
                         "statistics.paymentStats.averageExpectedAmount"
                       )
                     )}
                   </span>
                 </div>
               </div>
             </div>

             <div className="bg-gray-50 rounded-lg p-3">
               <h5 className="font-medium text-gray-700 mb-2">Analyse</h5>
               <div className="space-y-1 text-sm">
                 <div className="flex justify-between">
                   <span>Avec versement:</span>
                   <span className="font-medium">
                     {getSafeValue(
                       sortedData,
                       "statistics.paymentStats.contractsWithPayment"
                     )}
                   </span>
                 </div>
                 <div className="flex justify-between">
                   <span>Avec remise:</span>
                   <span className="font-medium">
                     {getSafeValue(
                       sortedData,
                       "statistics.paymentStats.contractsWithDiscount"
                     )}
                   </span>
                 </div>
                 <div className="flex justify-between">
                   <span>Taux renouvellement:</span>
                   <span className="font-medium">
                     {getSafeValue(
                       sortedData,
                       "statistics.renewalStats.renewalPercentage"
                     )}
                     %
                   </span>
                 </div>
               </div>
             </div>
           </div>
         </div>

         {/* Section Contrats créés avec tri par agence et dossier */}
         <div className="bg-white border border-gray-200 rounded-lg p-4">
           <h4 className="text-lg font-semibold mb-4">
             📋 Contrats créés ce jour (
             {sortedData.contractsCreated?.length || 0})
             <span className="text-sm font-normal text-gray-600 ml-2">
               (Triés par agence puis par n° dossier)
             </span>
           </h4>

           {!sortedData.contractsCreated ||
           sortedData.contractsCreated.length === 0 ? (
             <div className="text-center text-gray-500 py-8">
               <div className="text-4xl mb-2">📄</div>
               <p>Aucun contrat créé ce jour</p>
             </div>
           ) : (
             <div className="overflow-x-auto">
               <table className="min-w-full table-auto text-sm">
                 <thead className="bg-gray-50">
                   <tr>
                     <th className="px-2 py-2 text-left font-medium text-gray-700">
                       Agence
                     </th>
                     <th className="px-2 py-2 text-left font-medium text-gray-700">
                       Contrat
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700 hidden md:table-cell">
                       Dossier
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700">
                       P.bijou
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700 hidden md:table-cell">
                       CDVA
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700 hidden md:table-cell">
                       CDD
                     </th>
                     <th className="px-2 py-2 text-center font-medium text-gray-700 hidden lg:table-cell">
                       Création
                     </th>
                     <th className="px-2 py-2 text-center font-medium text-gray-700 hidden lg:table-cell">
                       Limite
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700">
                       Décais.
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700">
                       Prévu
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700 hidden sm:table-cell">
                       Perçu
                     </th>
                     <th className="px-2 py-2 text-right font-medium text-gray-700 hidden sm:table-cell">
                       Reste
                     </th>
                   </tr>
                 </thead>
                 <tbody>
                   {sortedData.contractsCreated.map((contract, index) => (
                     <tr
                       key={`contract-created-${
                         contract.contractNumber || "unknown"
                       }-${index}`}
                       className={index % 2 === 0 ? "bg-gray-25" : "bg-white"}
                     >
                       <td className="px-2 py-2 font-medium text-xs sm:text-sm">
                         {contract.agency || "N/A"}
                       </td>
                       <td className="px-2 py-2 font-medium text-xs sm:text-sm">
                         {contract.contractNumber || "N/A"}
                       </td>
                       <td className="px-2 py-2 text-right text-xs hidden md:table-cell font-medium">
                         {contract.dossier || "-"}
                       </td>
                       <td className="px-2 py-2 text-right text-xs">
                         {formatDecimal(contract.jewelWeight)}
                       </td>
                       <td className="px-2 py-2 text-right text-xs hidden md:table-cell">
                         {formatDecimal(contract.cdvaWeight)}
                       </td>
                       <td className="px-2 py-2 text-right text-xs hidden md:table-cell">
                         {formatDecimal(contract.cddWeight)}
                       </td>
                       <td className="px-2 py-2 text-center text-xs hidden lg:table-cell">
                         {formatDate(contract.creationDate)}
                       </td>
                       <td className="px-2 py-2 text-center text-xs hidden lg:table-cell">
                         {formatDate(contract.limitDate)}
                       </td>
                       <td className="px-2 py-2 text-right text-xs font-medium">
                         {formatAmount(contract.disbursementAmount)}
                       </td>
                       <td className="px-2 py-2 text-right text-xs font-medium">
                         {formatAmount(contract.expectedAmount)}
                       </td>
                       <td className="px-2 py-2 text-right text-xs hidden sm:table-cell">
                         {formatAmount(contract.receivedAmount)}
                       </td>
                       <td className="px-2 py-2 text-right text-xs hidden sm:table-cell">
                         {formatAmount(contract.remainingAmount)}
                       </td>
                     </tr>
                   ))}
                 </tbody>
               </table>
             </div>
           )}
         </div>

{/* Section États versement avec hover et double-clic */}
<div className="bg-white border border-gray-200 rounded-lg p-4">
  <h4 className="text-lg font-semibold mb-4">
    💰 États des versements ({sortedData.paymentStates?.length || 0})
    <span className="text-sm font-normal text-gray-600 ml-2">
      (Triés par agence puis par n° contrat)
    </span>
  </h4>

  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
      <div className="flex-1">
        <p className="text-sm text-gray-700 font-medium mb-1">
          Contrats avec activité ce jour :
        </p>
        <div className="text-xs text-gray-600 space-y-1">
          <div>
            • Versements du {filters.day}/{filters.month}/{filters.year}
          </div>
          <div>
            • Clôtures du {filters.day}/{filters.month}/{filters.year}
          </div>
          <div className="text-orange-600 font-medium mt-2">
            💡 Double-cliquez sur une ligne pour voir les détails des versements
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="text-sm text-green-600">Total perçu</div>
        <div className="text-xl font-bold text-green-600">
          {formatAmount(
            getSafeValue(
              sortedData,
              "paymentStatesTotals.totalReceived"
            )
          )}
        </div>

        <div className="text-sm text-blue-600 mt-2">
          Total perçu du jour
        </div>
        <div className="text-lg font-bold text-blue-600">
          {formatAmount(
            getSafeValue(
              sortedData,
              "paymentStatesTotals.dailyReceived"
            )
          )}
        </div>

        {getSafeValue(
          sortedData,
          "paymentStatesTotals.totalDiscount"
        ) > 0 && (
          <>
            <div className="text-xs text-purple-600 mt-1">
              Total remises
            </div>
            <div className="text-sm font-semibold text-purple-600">
              {formatAmount(
                getSafeValue(
                  sortedData,
                  "paymentStatesTotals.totalDiscount"
                )
              )}
            </div>
          </>
        )}
      </div>
    </div>
  </div>

  {!sortedData.paymentStates ||
  sortedData.paymentStates.length === 0 ? (
    <div className="text-center text-gray-500 py-8">
      <div className="text-4xl mb-2">💳</div>
      <p>Aucune activité de versement ou clôture ce jour</p>
    </div>
  ) : (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 text-left font-medium text-gray-700">
              Agence
            </th>
            <th className="px-2 py-2 text-left font-medium text-gray-700">
              Contrat
            </th>
            <th className="px-2 py-2 text-right font-medium text-gray-700">
              Total Perçu
            </th>
            <th className="px-2 py-2 text-right font-medium text-blue-700 bg-blue-50 border-l-2 border-blue-200">
              Perçu ce jour
            </th>
            <th className="px-2 py-2 text-right font-medium text-gray-700 hidden md:table-cell">
              Remise
            </th>
            <th className="px-2 py-2 text-center font-medium text-gray-700 hidden lg:table-cell">
              Rmq
            </th>
            <th className="px-2 py-2 text-center font-medium text-gray-700">
              Type
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.paymentStates.map((contract, index) => (
            <tr
              key={`payment-state-${
                contract.contractNumber || "unknown"
              }-${index}`}
              className={`${
                index % 2 === 0 ? "bg-gray-25" : "bg-white"
              } hover:bg-blue-50 cursor-pointer transition-colors duration-200`}
              onDoubleClick={() => handleContractDoubleClick(contract.contractNumber)}
              title="Double-cliquez pour voir les détails des versements"
            >
              <td className="px-2 py-2 font-medium text-xs sm:text-sm">
                {contract.agency || "N/A"}
              </td>
              <td className="px-2 py-2 font-medium text-xs sm:text-sm">
                {contract.contractNumber || "N/A"}
              </td>
              <td className="px-2 py-2 text-right text-xs">
                <span
                  className={
                    contract.receivedAmount > 0
                      ? "text-green-600 font-medium"
                      : ""
                  }
                >
                  {formatAmount(contract.receivedAmount)}
                </span>
              </td>
              <td className="px-2 py-2 text-right text-xs bg-blue-25 border-l-2 border-blue-100">
                <span
                  className={`font-bold ${
                    contract.dailyPayment > 0
                      ? "text-blue-700"
                      : "text-gray-400 italic"
                  }`}
                >
                  {contract.dailyPayment > 0
                    ? formatAmount(contract.dailyPayment)
                    : "0,00"}
                </span>
              </td>
              <td className="px-2 py-2 text-right text-xs hidden md:table-cell">
                <span
                  className={
                    contract.discount > 0
                      ? "text-purple-600 font-medium"
                      : ""
                  }
                >
                  {contract.discount > 0
                    ? formatAmount(contract.discount)
                    : "-"}
                </span>
              </td>
              <td className="px-2 py-2 text-center hidden lg:table-cell">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    contract.remark === "R"
                      ? "bg-green-100 text-green-800"
                      : contract.remark === "NR"
                      ? "bg-red-100 text-red-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {contract.remark === "R"
                    ? "R"
                    : contract.remark === "NR"
                    ? "NR"
                    : contract.remark || "-"}
                </span>
              </td>
<td className="px-2 py-2 text-center">
  {/* 🆕 NOUVEAU : Type avec nombre de versements */}
  <div className="flex flex-col items-center space-y-1">
    <span
      className={`px-1 py-1 rounded text-xs font-medium ${
        contract.type === "versement"
          ? "bg-blue-100 text-blue-800"
          : contract.type === "cloture"
          ? "bg-purple-100 text-purple-800"
          : contract.type === "versement+cloture"
          ? "bg-green-100 text-green-800"
          : "bg-gray-100 text-gray-600"
      }`}
    >
      {contract.type === "versement"
        ? "Versement"
        : contract.type === "cloture"
        ? "Clôturé"
        : contract.type === "versement+cloture"
        ? "Versement+Clôturé"
        : "-"}
      {contract.totalPaymentsCount > 0 && (
        <span 
          className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${
            contract.hasTypeVP4 
              ? "text-red-600 bg-transparent" 
              : "text-blue-800 bg-transparent"
          }`}
        >
          {contract.totalPaymentsCount}
        </span>
      )}
    </span>
  </div>
</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )}
</div>


         {/* Informations supplémentaires */}
         {sortedData.statistics?.uniqueAgencies &&
           sortedData.statistics.uniqueAgencies.length > 0 && (
             <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
               <h4 className="text-lg font-semibold mb-2 text-blue-800">
                 🏢 Agences concernées
               </h4>
               <div className="flex flex-wrap gap-2">
                 {sortedData.statistics.uniqueAgencies.map((agency, index) => (
                   <span
                     key={`unique-agency-${agency || "unknown"}-${index}`}
                     className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs sm:text-sm font-medium"
                   >
                     {agency || "N/A"}
                   </span>
                 ))}
               </div>
             </div>
           )}
       </>
     )}

     {/* Message si aucune donnée disponible */}
     {!isLoading &&
       !sortedData &&
       filters.year &&
       filters.month &&
       filters.day && (
         <div className="text-center text-gray-500 py-12">
           <div className="text-6xl mb-4">📅</div>
           <h3 className="text-xl font-medium text-gray-900 mb-2">
             Aucune donnée disponible
           </h3>
           <p className="text-gray-600">
             Aucune donnée trouvée pour le {filters.day}/{filters.month}/
             {filters.year}
           </p>
           <p className="text-sm text-gray-500 mt-2">
             Essayez une autre date ou vérifiez les filtres
           </p>
         </div>
       )}
   </div>
 );
};

export default DailyStatesTab;